import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import type { Goal } from './useGoals';
import { calcGoal } from './useGoals';

export type InsightType = 'warning' | 'success' | 'info' | 'tip';

export interface Insight {
  id: string;
  type: InsightType;
  title: string;
  message: string;
  icon: string;
  relatedGoalId?: string;
  value?: number;
}

interface CategoryTotal {
  category: string;
  total: number;
}

export const useFinancialInsights = (goals: Goal[]) => {
  const { user } = useAuth();
  const [insights, setInsights] = useState<Insight[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingsRate, setSavingsRate] = useState<number | null>(null);
  const [topCategory, setTopCategory] = useState<CategoryTotal | null>(null);
  const [monthExpenses, setMonthExpenses] = useState(0);
  const [monthIncome, setMonthIncome] = useState(0);
  const [prevMonthExpenses, setPrevMonthExpenses] = useState(0);

  const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const generateInsights = useCallback(async () => {
    if (!user) return;
    setLoading(true);

    const today = new Date();
    const curStart = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
    const curEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];
    const prevStart = new Date(today.getFullYear(), today.getMonth() - 1, 1).toISOString().split('T')[0];
    const prevEnd = new Date(today.getFullYear(), today.getMonth(), 0).toISOString().split('T')[0];

    const [{ data: curTxs }, { data: prevTxs }] = await Promise.all([
      // CRÍTICO: deleted_at filter + transaction_subtype para excluir transferências
      supabase.from('transactions').select('type, amount, category, status, transaction_subtype').eq('user_id', user.id).gte('date', curStart).lte('date', curEnd).is('deleted_at', null),
      supabase.from('transactions').select('type, amount, category, status, transaction_subtype').eq('user_id', user.id).gte('date', prevStart).lte('date', prevEnd).is('deleted_at', null),
    ]);

    const cur = curTxs ?? [];
    const prev = prevTxs ?? [];

    // REGRA: usar apenas realizados — status='pago' para despesas, 'recebido' para receitas
    // CRÍTICO: excluir transferências dos cálculos — evita dupla contagem
    const isIncome = (t: any) => (t.type === 'receita' || t.type === 'income') && t.status === 'recebido' && t.transaction_subtype !== 'transferencia';
    const isExpense = (t: any) => (t.type === 'despesa' || t.type === 'expense') && t.status === 'pago' && t.transaction_subtype !== 'transferencia';

    const curExpenses = cur.filter(isExpense).reduce((s, t) => s + Number(t.amount), 0);
    const curIncome = cur.filter(isIncome).reduce((s, t) => s + Number(t.amount), 0);
    const prevExpenses = prev.filter(isExpense).reduce((s, t) => s + Number(t.amount), 0);

    setMonthExpenses(curExpenses);
    setMonthIncome(curIncome);
    setPrevMonthExpenses(prevExpenses);

    const rate = curIncome > 0 ? ((curIncome - curExpenses) / curIncome) * 100 : null;
    setSavingsRate(rate);

    const catMap: Record<string, number> = {};
    cur.filter(isExpense).forEach(t => {
      catMap[t.category] = (catMap[t.category] || 0) + Number(t.amount);
    });

    const cats: CategoryTotal[] = Object.entries(catMap)
      .map(([category, total]) => ({ category, total }))
      .sort((a, b) => b.total - a.total);

    const top = cats[0] ?? null;
    setTopCategory(top);

    const newInsights: Insight[] = [];
    let idx = 0;

    // Top spending category
    if (top) {
      newInsights.push({
        id: `cat-top-${idx++}`,
        type: 'info',
        icon: 'ri-pie-chart-line',
        title: `Maior gasto: ${top.category}`,
        message: `Você gastou ${fmt(top.total)} com ${top.category} este mês${cats.length > 1 ? `, representando ${Math.round((top.total / curExpenses) * 100)}% das suas despesas` : ''}.`,
        value: top.total,
      });
    }

    // Month-over-month comparison
    if (prevExpenses > 0) {
      const diff = curExpenses - prevExpenses;
      const diffPct = Math.abs(Math.round((diff / prevExpenses) * 100));
      if (diff > 0) {
        newInsights.push({
          id: `mom-up-${idx++}`,
          type: diffPct > 20 ? 'warning' : 'info',
          icon: 'ri-arrow-up-circle-line',
          title: 'Gastos acima do mês anterior',
          message: `Seus gastos subiram ${diffPct}% em relação ao mês passado (${fmt(diff)} a mais). Isso pode impactar o progresso das suas metas.`,
          value: diff,
        });
      } else if (diff < 0) {
        newInsights.push({
          id: `mom-down-${idx++}`,
          type: 'success',
          icon: 'ri-arrow-down-circle-line',
          title: 'Gastos abaixo do mês anterior',
          message: `Parabéns! Você reduziu seus gastos em ${diffPct}% (${fmt(Math.abs(diff))} a menos). Ótima oportunidade para acelerar suas metas.`,
          value: Math.abs(diff),
        });
      }
    }

    // Savings rate
    if (rate !== null) {
      if (rate < 0) {
        newInsights.push({
          id: `savings-neg-${idx++}`,
          type: 'warning',
          icon: 'ri-alert-line',
          title: 'Gastos superaram a receita',
          message: `Este mês suas despesas (${fmt(curExpenses)}) superaram suas receitas (${fmt(curIncome)}). Revise seus gastos para proteger suas metas.`,
        });
      } else if (rate < 10) {
        newInsights.push({
          id: `savings-low-${idx++}`,
          type: 'warning',
          icon: 'ri-wallet-line',
          title: 'Capacidade de poupança baixa',
          message: `Você está poupando apenas ${rate.toFixed(0)}% da sua renda este mês. Para atingir suas metas no prazo, tente aumentar esse percentual.`,
          value: rate,
        });
      } else if (rate >= 20) {
        newInsights.push({
          id: `savings-good-${idx++}`,
          type: 'success',
          icon: 'ri-money-dollar-circle-line',
          title: 'Boa taxa de poupança',
          message: `Você está poupando ${rate.toFixed(0)}% da sua renda este mês (${fmt(curIncome - curExpenses)}). Continue assim e suas metas estarão em boa posição.`,
          value: rate,
        });
      }
    }

    // Per-goal insights
    const activeGoals = goals.filter(g => g.status === 'active' && !g.is_completed);
    const monthlySavingsAvailable = curIncome > curExpenses ? curIncome - curExpenses : 0;

    activeGoals.forEach(goal => {
      const calc = calcGoal(goal);

      if (calc.pace === 'overdue') {
        newInsights.push({
          id: `goal-overdue-${goal.id}-${idx++}`,
          type: 'warning',
          icon: 'ri-time-line',
          title: `Meta "${goal.name}" vencida`,
          message: `A data alvo desta meta já passou. Faltam ainda ${fmt(calc.remaining)}. Revise a data ou reforce os aportes.`,
          relatedGoalId: goal.id,
          value: calc.remaining,
        });
      } else if (calc.pace === 'behind') {
        newInsights.push({
          id: `goal-behind-${goal.id}-${idx++}`,
          type: 'warning',
          icon: 'ri-flag-line',
          title: `Meta "${goal.name}" está atrasada`,
          message: `No ritmo atual, você não atingirá essa meta até ${goal.target_date ? new Date(goal.target_date + 'T12:00:00').toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }) : 'a data alvo'}. ${calc.monthlyNeeded ? `Guarde pelo menos ${fmt(calc.monthlyNeeded)}/mês para ficar em dia.` : ''}`,
          relatedGoalId: goal.id,
          value: calc.monthlyNeeded ?? undefined,
        });
      } else if (calc.pace === 'at_risk') {
        newInsights.push({
          id: `goal-risk-${goal.id}-${idx++}`,
          type: 'warning',
          icon: 'ri-error-warning-line',
          title: `Meta "${goal.name}" em risco`,
          message: `Essa meta está levemente atrasada em relação ao ritmo ideal. ${calc.monthlyNeeded ? `Aporte ${fmt(calc.monthlyNeeded)}/mês para recuperar o prazo.` : ''}`,
          relatedGoalId: goal.id,
          value: calc.monthlyNeeded ?? undefined,
        });
      } else if (calc.pace === 'on_track') {
        newInsights.push({
          id: `goal-ok-${goal.id}-${idx++}`,
          type: 'success',
          icon: 'ri-checkbox-circle-line',
          title: `Meta "${goal.name}" no ritmo certo`,
          message: `Você está progredindo bem nesta meta (${calc.progress.toFixed(0)}% concluído). ${calc.monthlyNeeded ? `Mantenha aportes de ${fmt(calc.monthlyNeeded)}/mês.` : ''}`,
          relatedGoalId: goal.id,
        });
      }

      // Can current savings cover this goal?
      if (calc.monthlyNeeded && monthlySavingsAvailable > 0 && monthlySavingsAvailable < calc.monthlyNeeded) {
        newInsights.push({
          id: `goal-cant-${goal.id}-${idx++}`,
          type: 'tip',
          icon: 'ri-lightbulb-line',
          title: `Sugestão para "${goal.name}"`,
          message: `Sua capacidade atual de poupança (${fmt(monthlySavingsAvailable)}/mês) está abaixo do necessário (${fmt(calc.monthlyNeeded)}/mês). Reduzir despesas ajudaria a atingir essa meta.`,
          relatedGoalId: goal.id,
        });
      }
    });

    // Tip when no goals
    if (goals.length === 0) {
      newInsights.push({
        id: `tip-goals-${idx++}`,
        type: 'tip',
        icon: 'ri-focus-3-line',
        title: 'Defina suas metas financeiras',
        message: 'Ter metas claras aumenta muito as chances de sucesso financeiro. Crie sua primeira meta para começar a acompanhar seu progresso.',
      });
    }

    setInsights(newInsights);
    setLoading(false);
  }, [user, goals]);

  useEffect(() => { generateInsights(); }, [generateInsights]);

  return {
    insights,
    loading,
    savingsRate,
    topCategory,
    monthExpenses,
    monthIncome,
    prevMonthExpenses,
    refetch: generateInsights,
  };
};
